import axios from "axios";

import { auth, createNewUser, getUserDoc } from "../../../../../firebase";
import { setAuthToken } from "utils/token.utils";
import { ClientCreateResult } from "types/clientUpload";
import { User } from "types/firebase";

const CHECK_BATCH_SIZE = 500;

// Tokens expire after an hour, so refresh before each request in a long batch
const refreshAuthToken = async () => {
  const token = await auth.currentUser?.getIdToken();
  setAuthToken(token);
};

// Asks the server which emails already have a login
export const checkExistingEmails = async (
  emails: string[]
): Promise<{ existing: string[]; invalid: string[] }> => {
  await refreshAuthToken();

  const existing: string[] = [];
  const invalid: string[] = [];

  for (let i = 0; i < emails.length; i += CHECK_BATCH_SIZE) {
    const response = await axios.post("/user/check-emails", {
      emails: emails.slice(i, i + CHECK_BATCH_SIZE),
    });
    existing.push(...response.data.existing);
    invalid.push(...response.data.invalid);
  }

  return { existing, invalid };
};

// Same two steps as the manual Add New User form: create the login on the
// server, then create the Firestore profile and link it to the company.
export const createClientAccount = async (client: {
  name: string;
  email: string;
  password: string;
  company: string;
  companyId: string;
}): Promise<ClientCreateResult> => {
  const { name, email, password, company, companyId } = client;
  let uid: string;

  try {
    await refreshAuthToken();
    const response = await axios.post("/user/create", {
      email,
      password,
      confirmPassword: password,
    });
    uid = String(response.data);
  } catch (err) {
    const error = err as any;
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status === 403) {
      return {
        state: "unauthorized",
        message:
          "Not authorized. Your session may have expired, or your account is not on the server admin allowlist.",
      };
    }

    if (data?.code === "auth/email-already-exists") return { state: "exists" };

    const message =
      (data && (data.message || data.email || data.password || data.confirmPassword)) ||
      error?.message ||
      "Request failed";

    return { state: "failed", message };
  }

  const notLinked = (detail: string): ClientCreateResult => ({
    state: "profile-failed",
    uid,
    message: `Login created but the profile was not linked (${detail}). Fix it in Client Access or the Firebase console.`,
  });

  // Firestore rejects undefined values, so optional fields are empty strings.
  // createNewUserObject sets role "standard" and enabled: true.
  const newUser: User & { country: string; category: string } = {
    uid,
    email,
    name,
    company,
    phone: "",
    title: "",
    country: "",
    category: "",
    role: "standard",
    favorites: [],
    customReports: { created: [], shared: [] },
    enabled: true,
  };

  try {
    await createNewUser(newUser);

    const doc = await getUserDoc(uid);
    if (!doc) return notLinked("no profile document");
    if (!doc.usergroupRef || doc.usergroupRef.id !== companyId) {
      return notLinked("company link missing or different");
    }
    if (doc.name !== name) return notLinked("profile name not saved");
  } catch (err) {
    const error = err as any;
    return notLinked(error?.message || "profile error");
  }

  return { state: "created", uid };
};
